import { useEffect, useRef, useState } from "react";
import { getTokenEndpointRateLimitedUntil } from "@osakabe/auth-service-provider";

// 共同編集の読み込み / 接続に失敗した時の自動リロード待機秒数の既定値。
// 認証レートリミット (429) 中はその解除予定時刻まで待つが、解除予定が分からない一過性の
// 失敗 (ネットワーク断など) では、この既定秒数だけ待ってから 1 回だけリロードする。
const DEFAULT_RELOAD_COUNTDOWN_SECONDS = 20;
// カウントダウン自動リロードを行う上限秒数。これを超える長時間ブロックでは延々カウントダウン
// しても待ち時間を浪費するだけなので、自動リロードせず手動ボタンへ委ねる。
const MAX_RELOAD_COUNTDOWN_SECONDS = 300;
// 自動リロードの連続回数の上限。これを超えたら自動リロードを止め、手動ボタンのみにする。
// 認証レートリミットは解除予定時刻まで待ってから 1 回だけ叩くため、MEIDEN CONNECT 認証基盤の
// IP 単位ブロックカウンタ (blockCount) を押し上げて WAF 永久ブロックへ至るリスクは無いが、
// 復旧しない失敗で無限にリロードし続けないよう保険として上限を設ける。
const MAX_AUTO_RELOAD_ATTEMPTS = 5;
const AUTO_RELOAD_ATTEMPT_COUNT_KEY = "refinement-canvas.load-error-auto-reload-count.v1";

interface ErrorReloadCountdownProps {
  // テスト用のフック。未指定時はページ全体を再読み込みする。
  readonly onReload?: (() => void) | undefined;
}

/**
 * 共同編集の読み込み / 接続失敗時に、カウントダウンを表示して 0 で 1 回だけ自動リロードする。
 *
 * - 認証レートリミット (429) 中は `@osakabe/auth-service-provider` が保持する解除予定時刻まで
 *   待ってからリロードする。**ブロック解除後に 1 回だけ叩く** ことで、ブロック中の連打により
 *   IdP 側の IP 単位ブロックカウンタが WAF 永久ブロックへ至るのを避ける。
 * - 解除予定が分からない一過性の失敗では既定秒数だけ待つ。
 * - 復旧しない失敗で無限リロードしないよう、連続自動リロード回数に上限を設ける。
 *   上限到達後は手動「今すぐ再読み込み」ボタンのみを表示する。
 */
export function ErrorReloadCountdown({ onReload }: ErrorReloadCountdownProps): JSX.Element {
  const reload = onReload ?? (() => window.location.reload());
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  });

  // マウント時に一度だけ、自動リロードの可否と初期カウントダウン秒数を確定する。
  // - 自動リロード回数が上限到達 → 手動ボタンのみ。
  // - レートリミットの残りが上限秒数 (300 秒) 超の長時間ブロック → 自動リロードせず手動委譲
  //   (途中で再読み込みすると 429 を再発させるため)。
  const [initialState] = useState(() => {
    const seconds = resolveInitialCountdownSeconds();
    const allowed = readAutoReloadAttemptCount() < MAX_AUTO_RELOAD_ATTEMPTS && seconds !== undefined;
    return { allowed, seconds: seconds ?? 0 };
  });
  const autoReloadAllowed = initialState.allowed;
  const [remainingSeconds, setRemainingSeconds] = useState(initialState.seconds);

  useEffect(() => {
    if (!autoReloadAllowed) {
      return undefined;
    }
    if (remainingSeconds <= 0) {
      // ブロック解除予定 (または既定待機) に到達。回数を記録して 1 回だけリロードする。
      writeAutoReloadAttemptCount(readAutoReloadAttemptCount() + 1);
      reloadRef.current();
      return undefined;
    }
    const timerId = window.setTimeout(() => {
      setRemainingSeconds((previous) => Math.max(0, previous - 1));
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [autoReloadAllowed, remainingSeconds]);

  return (
    <div className="loading-overlay-countdown" aria-live="polite">
      {autoReloadAllowed ? (
        <p>
          {remainingSeconds > 0
            ? `あと ${remainingSeconds} 秒で自動的に再読み込みします。`
            : "再読み込みしています…"}
        </p>
      ) : (
        <p>自動再読み込みを繰り返しても復旧しませんでした。下のボタンで再読み込みをお試しください。</p>
      )}
      <button
        type="button"
        className="loading-overlay-reload-button"
        onClick={() => {
          writeAutoReloadAttemptCount(readAutoReloadAttemptCount() + 1);
          reloadRef.current();
        }}
      >
        今すぐ再読み込み
      </button>
    </div>
  );
}

/** 読み込みが成功した時に、自動リロード回数カウンタをリセットする。 */
export function resetLoadErrorAutoReloadAttemptCount(): void {
  try {
    window.sessionStorage.removeItem(AUTO_RELOAD_ATTEMPT_COUNT_KEY);
  } catch {
    // sessionStorage が使えない環境では何もしない。
  }
}

/**
 * 初期カウントダウン秒数を決める。上限を超える長時間ブロックでは自動リロードを行わないため
 * `undefined` を返す（途中で再読み込みすると 429 を再発させてしまうため、手動操作へ委ねる）。
 */
function resolveInitialCountdownSeconds(): number | undefined {
  let seconds = DEFAULT_RELOAD_COUNTDOWN_SECONDS;
  try {
    const until = getTokenEndpointRateLimitedUntil();
    if (until !== undefined) {
      const remaining = Math.ceil((until - Date.now()) / 1000);
      if (remaining > 0) {
        seconds = remaining;
      }
    }
  } catch {
    // rate-limit 状態を読めない場合は既定秒数を使う。
  }
  if (seconds > MAX_RELOAD_COUNTDOWN_SECONDS) {
    // 上限超の長時間ブロックは自動リロードせず手動「今すぐ再読み込み」へ委ねる。
    return undefined;
  }
  return Math.max(1, seconds);
}

function readAutoReloadAttemptCount(): number {
  try {
    const raw = window.sessionStorage.getItem(AUTO_RELOAD_ATTEMPT_COUNT_KEY);
    if (raw === null) {
      return 0;
    }
    const value = Number.parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    // sessionStorage が読めない環境では、無限リロードを避けるため上限到達扱いにする。
    return MAX_AUTO_RELOAD_ATTEMPTS;
  }
}

function writeAutoReloadAttemptCount(count: number): void {
  try {
    window.sessionStorage.setItem(AUTO_RELOAD_ATTEMPT_COUNT_KEY, String(count));
  } catch {
    // sessionStorage が使えない環境では何もしない。
  }
}
